import { useForm } from "@inertiajs/react";
import type { FormEvent } from "react";

/**
 * Shared by sign-in and registration: both prove the David Lloyd credentials
 * still work, and neither stores the password — it is exchanged for a refresh
 * token and discarded.
 */
export function CredentialsForm({ action, submitLabel }: { action: string; submitLabel: string }) {
  const { data, setData, post, processing, errors } = useForm({ phone: "", password: "" });

  function submit(event: FormEvent) {
    event.preventDefault();
    post(action);
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <Field
        label="Mobile number"
        hint="The number you use for your club"
        value={data.phone}
        onChange={(v) => setData("phone", v)}
        type="tel"
        placeholder="+447700900123"
        autoComplete="tel"
        error={errors.phone}
      />
      <Field
        label="Club password"
        value={data.password}
        onChange={(v) => setData("password", v)}
        type="password"
        autoComplete="current-password"
        error={errors.password}
      />

      <p className="text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
        Your password is used once to connect your account and is never stored.
        Court Pilot keeps only the session token your club issues, and you can
        disconnect at any time.
      </p>

      <button
        type="submit"
        disabled={processing}
        className="w-full rounded-lg bg-brand-600 px-4 py-2.5 font-medium text-white transition hover:bg-brand-700 disabled:opacity-50"
      >
        {processing ? "Connecting…" : submitLabel}
      </button>
    </form>
  );
}

function Field({
  label, hint, value, onChange, type, placeholder, autoComplete, error,
}: {
  label: string; hint?: string; value: string; onChange: (v: string) => void;
  type: string; placeholder?: string; autoComplete?: string; error?: string;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      {hint && <span className="ml-2 text-xs text-neutral-500">{hint}</span>}
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-neutral-700 dark:bg-neutral-900"
        required
      />
      {error && <span className="mt-1 block text-sm text-red-600">{error}</span>}
    </label>
  );
}
